// ─── In-app alert / confirm dialogs ──────────────────────────────────────────
// Replacements for window.alert() and window.confirm(). On iOS those native
// WebView dialogs could come up out of sight and freeze the app (seen Oct 2026
// on the Blast screen), so the app uses its own sheet instead.
//
//   await showAlert('Profile saved!');
//   if (!(await showConfirm('Delete this listing?', { confirmLabel: 'Delete', danger: true }))) return;
//
// <DialogHost /> is mounted once in App.js and renders whatever is queued.
import { useEffect, useState } from 'react';

let queue = [];
let notify = null;

function enqueue(item) {
  return new Promise((resolve) => {
    queue = [...queue, { ...item, resolve }];
    if (notify) notify(queue);
  });
}

/** Show a message with an OK button. Resolves when dismissed. */
export function showAlert(message, { title, okLabel = 'OK' } = {}) {
  return enqueue({ kind: 'alert', message: String(message ?? ''), title, okLabel });
}

/** Ask a yes/no question. Resolves true (confirm) or false (cancel). */
export function showConfirm(message, { title, confirmLabel = 'OK', cancelLabel = 'Cancel', danger = false } = {}) {
  return enqueue({ kind: 'confirm', message: String(message ?? ''), title, confirmLabel, cancelLabel, danger });
}

export function DialogHost() {
  const [items, setItems] = useState(queue);
  useEffect(() => {
    notify = setItems;
    setItems(queue);
    return () => { if (notify === setItems) notify = null; };
  }, []);

  const current = items[0];
  if (!current) return null;

  const close = (value) => {
    queue = queue.slice(1);
    setItems(queue);
    current.resolve(value);
  };
  const isConfirm = current.kind === 'confirm';

  return (
    <div style={s.overlay} onClick={() => close(isConfirm ? false : undefined)}>
      <div style={s.sheet} role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {current.title && <h3 style={s.title}>{current.title}</h3>}
        <p style={s.message}>{current.message}</p>
        <div style={s.actions}>
          {isConfirm && (
            <button style={s.secondary} onClick={() => close(false)}>{current.cancelLabel}</button>
          )}
          <button
            style={{ ...s.primary, ...(current.danger ? s.danger : null) }}
            onClick={() => close(isConfirm ? true : undefined)}
            autoFocus
          >
            {isConfirm ? current.confirmLabel : current.okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'var(--bg-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '20px' },
  sheet: { width: '100%', maxWidth: '340px', backgroundColor: 'var(--bg-card)', borderRadius: '16px', padding: '22px 20px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' },
  title: { margin: '0 0 6px', fontSize: '17px', fontWeight: '800', color: 'var(--text-primary)' },
  message: { margin: '0 0 18px', fontSize: '15px', lineHeight: 1.45, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' },
  actions: { display: 'flex', gap: '8px' },
  primary: { flex: 1, padding: '12px', borderRadius: '11px', border: 'none', backgroundColor: 'var(--btn-solid-bg)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  danger: { backgroundColor: 'var(--danger)' },
  secondary: { flex: 1, padding: '12px', borderRadius: '11px', border: '1px solid var(--border-card)', backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
};
